export default `# Class Components

In the first chapter, you were using functional components, such as the \`Title\` component:
\`\`\`plainJavascript
const Title = ({ title }) => (
  <h1>{title}</h1>
)
\`\`\`

You learned that functional components receive a \`props\` object to get bits of information from parent components.

You will often need to create components that are smarter than that. Components that can encapsulate additional logic.

The second type of a React component is a class component. We can create it using a JavaScript class:

\`\`\`react
class Title extends React.Component {
  render() {
    return <h1>Sweet Pumpkins</h1>
  }
}

render(
  <Title />
)
\`\`\`

The new component inherits all the properties and methods of the \`React.Component\` class.

It can be as simple, as the \`Title\` component above, or way more complex. Whatever the level of complexity, we must always make sure that the
component always has a \`render\` function that returns what it wants to display.

So when and why should we use class components?

Imagine that our component does not only display a title, but also a button to upvote the movie.
When you press the button, the number of upvotes should increase.

Such component would need a temporary memory where it could store the number of upvotes.
It should have also a "click" event listener that would listen to clicks on the button and increase the number of upvotes in real time.

Class components can store values internally in a \`state\` object.

The \`state\` object is initialised in the constructor method of the class when the component is instantiated.

Later, we can access the properties stored in component's \`state\`.

A component that has a \`state\` is called a stateful component.

Let's create a stateful \`Title\` component that will have a \`counter\` value stored in the \`state\`:

\`\`\`react
class Title extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      counter: 0
    }
  }

  render() {
    return (
      <h1>
        <span>{this.state.counter}</span>
        Sweet Pumpkins
      </h1>
    )
  }
}

render(
  <Title />
)
\`\`\`

Try to change the value of the \`counter\` in the \`Title\`'s constructor above and see how React immediately adjusts the value displayed by:
\`\`\`plainJavascript
<span>{this.state.counter}</span>
\`\`\`

When the \`Title\` component gets instantiated,
the constructor method is called. At first, it calls the constructor of its parent — \`React.Component\` —  and passes \`props\` to it:
\`\`\`plainJavascript
  super(props);
\`\`\`

The \`super\` method is a special method to call the constructor of the parent class.

In the next step the constructor creates a state object with a \`counter\` property:
\`\`\`plainJavascript
  this.state = {
    counter: 0
  }
\`\`\`

Once the \`state\` has been created, you will be able to access its properties.

For example, to access the \`counter\`, you will use the \`this.state.counter\`.

That's why, to display the \`counter\` value in the render method you can write:

\`\`\`plainJavascript
<span>{this.state.counter}</span>
\`\`\`

Being able to set the \`state\` only in the constructor would be quite limiting. But React gives us tools to change the \`state\` after
the component has been rendered on screen.

To respond to the events that happen inside of the component you can create \`event handler\` methods.

Let's add an upvote button and attach the \`onClick\` event handler method to it. Once the button gets clicked, the \`counter\` in the \`state\` should
increase by \`1\`:

\`\`\`react
class Title extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      counter: 0
    }
    this.onClick = this.onClick.bind(this);
  }

  onClick() {
    this.setState({ counter: this.state.counter + 1 });
  }

  render() {
    return (
      <div>
        <button onClick={this.onClick}>Upvote movie</button>
        <h1>
          <span>{this.state.counter}</span>
          Sweet Pumpkins
        </h1>
      </div>
    )
  }
}

render(
  <Title />
)
\`\`\`

You can't change the state directly like this:
\`\`\`plainJavascript
  this.state.counter = new_value;
\`\`\`

React lets you update the state by using the \`this.setState\` method. It takes an object as an argument. In our case, we are setting
the \`counter\` to the previous \`counter\`'s value plus \`1\`:
\`\`\`plainJavascript
  this.setState({ counter: this.state.counter + 1 });
\`\`\`

The \`setState\` method merges the new object with the old \`state\`.

If you had more properties in the \`state\`,
only the \`counter\` would have been overwritten, and the remaining properties would have been left intact.

When you use the \`setState\` method, React not only assigns a new value to a specific property on the \`state\` but it also re-renders the component.

So, when you click on an upvote button, the \`this.state.counter\` changes and the component re-renders. That's why you can immediately see the new value
of the \`counter\` on your screen.

The only place where you can assign the value directly to the \`state\` is in the constructor.

Once you define the \`onClick\` method, you have to attach it to the class using the \`bind\` method in the constructor:

\`\`\`plainJavascript
  this.onClick = this.onClick.bind(this);
\`\`\`

The \`onClick\` method is assigned to the \`onClick\` event handler property of the \`button\`.

The \`onClick\` is one of many standardised events in React. According to the [React events docs](https://reactjs.org/docs/events.html),
React normalises events so they have consistent properties across different browsers.

With everything wired up, you can test the \`Title\` component.
When you click on the button, the \`this.state.counter\` increases and the component rerenders the \`counter\`'s value.

To increase the \`counter\` you passed an object to the \`setState\` method. Another option is to pass a function
that takes \`state\` as an argument and returns an object that will be merged back into the \`state\`:
\`\`\`plainJavascript
  this.setState( state => ({ counter: this.state.counter + 1 }) );
\`\`\`

To make the code more readable, let's create a new method — \`increaseCounter\` – and pass it to the \`this.setState\`.

\`\`\`react
class Title extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      counter: 0
    }
    this.onClick = this.onClick.bind(this);
    this.increaseCounter = this.increaseCounter.bind(this);
  }

  increaseCounter(state) {
    return { counter: state.counter + 1 };
  }

  onClick() {
    this.setState(this.increaseCounter);
  }

  render() {
    return (
      <div>
        <button onClick={this.onClick}>Upvote movie</button>
        <h1>
          <span>{this.state.counter}</span>
          Sweet Pumpkins
        </h1>
      </div>
    )
  }
}

render(
  <Title />
)
\`\`\`

Notice, that:
- the \`increaseCounter\` method has to be bound to \`this\` in the constructor
- you have to pass the \`this.increaseCounter\` method rather than the result of \`this.inscreaseCounter\` method's call:
- the \`increaseCounter\` method has to receive a \`state\` as an argument like in the example above

## Adding Styles

We've learned how to create functional and class components but we haven't learned how to style them.

There are many methods of styling React components. Here, we will use inline styles.

To add an inline style to a component you have to create an object that contains CSS-like styles.
Then, you can assign it to the \`style\` property of the component.

There are three things to remember when working with inline styles:
- Inline styles are placed in an object – so don't put semicolons at the end of the line,
- Values assigned to properties have to be wrapped in quotes,
- Instead of using the \`line-height\`, you will use the camel notation: \`lineHeight\`

Let's see what it looks like by adding inline styles to the button:

\`\`\`react
const buttonStyles = {
    lineHeight: "30px",
    padding: "0 10px",
    background: "orange",
    color: "white",
    border: "0",
    borderRadius: "0",
    cursor: "pointer"
}

class Title extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      counter: 0
    }
    this.onClick = this.onClick.bind(this);
    this.increaseCounter = this.increaseCounter.bind(this);
  }

  increaseCounter(state) {
    return { counter: state.counter + 1 };
  }

  onClick() {
    this.setState(this.increaseCounter);
  }

  render() {
    return (
      <div>
        <button onClick={this.onClick} style={buttonStyles}>Upvote movie</button>
        <h1>
          <span>{this.state.counter}</span>
          Sweet Pumpkins
        </h1>
      </div>
    )
  }
}

render(
  <Title />
)
\`\`\`

## Summary

In this chapter, you've learned how to create class components and how to work with the constructor method.
You've wired up a button with the \`onClick\` event handler method to update the state.

There are two ways of updating the state:
- passing to the \`setState\` method an object that will be merged with the \`state\`
- passing to the \`setState\` method a function that takes the \`state\` as an argument and returns an object that will be merged with the \`state\`

Finally, you've learned how to add inline styles to a React component.


























`;



// WEBPACK FOOTER //
// ./src/book/chapters/react-class-components.js